import React from 'react';
import StaticContentPage from '../components/StaticContentPage';
import { Shield, Info, Users, AlertOctagon, HelpCircle, Eye, RefreshCw, Mail } from 'lucide-react';

const PolicySection = ({ icon: Icon, title, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 rounded-xl bg-brand-indigo/10 text-brand-indigo">
        <Icon size={20} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{title}</h2>
    </div>
    <div className="text-gray-600 leading-relaxed space-y-4 ml-1">
      {children}
    </div>
  </div>
);

const ContentPolicy = () => {
  return (
    <StaticContentPage title="Content Policy" lastUpdated="May 14, 2026">
      <div className="max-w-3xl mb-12">
        <p className="text-xl font-medium text-gray-700 leading-relaxed mb-6">
          Welcome to Chooziq.
        </p>
        <p className="text-gray-600 leading-relaxed">
          This Content Policy outlines the standards and rules governing content shared through the Chooziq platform, including communities, live rooms, discussions, brainstorming boards, polls, comments, uploaded materials, and other user-generated content.
        </p>
      </div>

      <PolicySection icon={Info} title="1. Purpose of This Policy">
        <p>Chooziq is designed to support realtime interaction, collaborative engagement, community participation, and live collaborative experiences. This policy helps ensure that content shared remains lawful, respectful, and appropriate for collaborative environments.</p>
      </PolicySection>

      <PolicySection icon={Users} title="2. User Responsibility">
        <p>Users are solely responsible for the content they create, upload, post, share, or distribute through the Services. Users must ensure that their content complies with applicable laws, intellectual property rights, and community guidelines.</p>
      </PolicySection>

      <PolicySection icon={RefreshCw} title="3. Permitted Content">
        <p>Chooziq encourages constructive and collaborative content, including educational discussions, professional conversations, workshop collaboration, brainstorming, and audience participation.</p>
      </PolicySection>

      <PolicySection icon={AlertOctagon} title="4. Prohibited Content">
        <div className="space-y-6">
          {[
            { t: "Illegal Content", d: "Content that violates laws or regulations, including fraud or unlawful conduct." },
            { t: "Harassment and Abuse", d: "Content intended to harass, threaten, or abuse others, including hate speech." },
            { t: "Harmful Content", d: "Promotion of violence, self-harm, terrorism, or harmful misinformation." },
            { t: "Sexually Explicit Content", d: "Inappropriate adult content is prohibited in all collaborative environments." },
            { t: "Spam & Manipulation", d: "Distribution of spam, scams, or attempts to manipulate engagement metrics." },
            { t: "Malicious Technology", d: "Uploading malware, viruses, phishing links, or harmful software." },
            { t: "IP Violations", d: "Content that infringes upon copyrights, trademarks, or patents." },
            { t: "Impersonation", d: "Falsely representing identity or authority is strictly prohibited." }
          ].map((item, idx) => (
            <div key={idx}>
              <h4 className="font-bold text-gray-900 mb-2">{item.t}</h4>
              <p className="text-sm text-gray-600">{item.d}</p>
            </div>
          ))}
        </div>
      </PolicySection>

      <PolicySection icon={HelpCircle} title="5. Realtime Interaction Standards">
        <p>Users are expected to interact responsibly during live sessions. Disruptive behavior such as flooding interactions, spamming reactions, or manipulating votes is prohibited.</p>
      </PolicySection>

      <PolicySection icon={Shield} title="6. Community and Organizer Rules">
        <p>Communities and moderators may establish additional content rules. Users must comply with community-specific policies and moderator instructions.</p>
      </PolicySection>

      <PolicySection icon={Eye} title="7. Reporting Content">
        <p>Users may report content that violates this policy. Reports are reviewed by moderators, administrators, or the Chooziq team.</p>
      </PolicySection>

      <PolicySection icon={Shield} title="8. Moderation and Enforcement">
        <p>Chooziq reserves the right to review reported content, remove material, or suspend accounts where necessary to maintain platform safety.</p>
      </PolicySection>

      <div className="bg-gray-50 p-10 rounded-[40px] border border-gray-100 mt-16 text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h3>
        <p className="text-gray-600 mb-6">For questions regarding this Content Policy or to report violations, please contact us.</p>
        <div className="flex flex-col items-center gap-2">
          <a href="mailto:support@chooziq.com" className="text-lg font-bold text-brand-indigo hover:underline">support@chooziq.com</a>
        </div>
      </div>
    </StaticContentPage>
  );
};

export default ContentPolicy;
